import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, update} from '../services/ProductsService';
import {Product} from '../types';
import {stripReadonlyFields} from '../../../utils/stripReadonlyFields';

export const useProduct = (productId?: string) => {
    const queryClient = useQueryClient();

    const productQuery = useQuery({
        queryKey: ['product', productId],
        queryFn: () => fetch(productId!),
        enabled: !!productId
    });

    const createMutation = useMutation({
        mutationFn: (values: Product) => {
            return add(stripReadonlyFields(values));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['products']});
        }
    });

    const updateMutation = useMutation({
        mutationFn: (values: Product) => {
            if (!productId) throw new Error('productId is required parameter');
            return update(productId, stripReadonlyFields(values) as Product);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['products']});
            queryClient.invalidateQueries({queryKey: ['product', productId]});
        }
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!productId) throw new Error('productId is required parameter');
            return deleteRecord(productId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['products']});
        }
    });

    return {
        record: productQuery.data,
        loading: productQuery.isLoading,
        fetchRecord: productQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};
